import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const RealTimeWeatherDashboard = () => {
  const [dataPoints, setDataPoints] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [wsConnection, setWsConnection] = useState(null);
  const maxDataPoints = 20;

  // Define all metrics to be displayed
  const metrics = [
    { value: 'temperature', label: 'Temperature (°C)', color: '#f87171' },
    { value: 'relative_humidity', label: 'Humidity (%)', color: '#60a5fa' },
    { value: 'wind_speed', label: 'Wind Speed (m/s)', color: '#34d399' },
    { value: 'baro_press', label: 'Barometric Pressure', color: '#a78bfa' },
    { value: 'rain', label: 'Rainfall', color: '#3b82f6' }
  ];

  useEffect(() => {
    try {
      const authorizedDevices = JSON.parse(localStorage.getItem('device_ids') || "[]");
      setAvailableDevices(authorizedDevices);
      if (authorizedDevices.length > 0) {
        setSelectedDevice(authorizedDevices[0]);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  }, []);

  useEffect(() => {
    if (!selectedDevice) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    if (wsConnection && (wsConnection.readyState === WebSocket.OPEN || wsConnection.readyState === WebSocket.CONNECTING)) {
      wsConnection.close();
    }

    setConnectionStatus('Connecting...');
    setDataPoints([]);

    const ws = new WebSocket(`/api/ws/${selectedDevice}?token=${token}`);

    ws.onopen = () => {
      console.log(`Connected to device: ${selectedDevice}`);
      setConnectionStatus('Connected');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const timestamp = new Date().getTime(); // Get milliseconds since epoch

        // Create a point with timestamp and all metrics
        const newPoint = { timestamp };
        
        // Add all metrics to the data point
        metrics.forEach(metric => {
          if (typeof data[metric.value] === 'number' && !isNaN(data[metric.value])) {
            newPoint[metric.value] = data[metric.value];
          }
        });

        setDataPoints(prevData => {
          const updatedData = [...prevData, newPoint];
          return updatedData.length > maxDataPoints
            ? updatedData.slice(-maxDataPoints)
            : updatedData;
        });
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    };

    ws.onclose = () => {
      console.log(`Disconnected from device: ${selectedDevice}`);
      setConnectionStatus('Disconnected');
    };

    ws.onerror = (error) => {
      console.error('WebSocket Error:', error);
      setConnectionStatus('Error');
    };

    setWsConnection(ws);

    return () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    };
  }, [selectedDevice]);

  // Filter out any data points with NaN or undefined values for timestamp
  const validDataPoints = dataPoints.filter(point => 
    point.timestamp !== undefined && 
    !isNaN(point.timestamp)
  );

  const renderGraph = (metric) => {
    return (
      <div key={metric.value} className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-2">{metric.label}</h2>
        <div className="h-64">
          {validDataPoints.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={validDataPoints}
                margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  type="number"
                  scale="time"
                  domain={['auto', 'auto']}
                  tickFormatter={(timestamp) => {
                    if (!timestamp) return '';
                    const date = new Date(timestamp);
                    return `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
                  }}
                />
                <YAxis domain={['auto', 'auto']} />
                <Tooltip 
                  formatter={(value) => {
                    return value !== undefined && !isNaN(value) 
                      ? [value.toFixed(2), metric.label]
                      : ['N/A', metric.label];
                  }}
                  labelFormatter={(timestamp) => {
                    if (!timestamp) return 'Time: N/A';
                    const date = new Date(timestamp);
                    return `Time: ${date.toLocaleTimeString()}`;
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey={metric.value} 
                  stroke={metric.color} 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                  connectNulls={true}
                  name={metric.label}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center">
              <p className="text-gray-500">
                {!selectedDevice 
                  ? 'Please select a device to view data'
                  : connectionStatus === 'Connected' 
                    ? 'Waiting for data...' 
                    : 'Connect to a device to view data'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">

      <div className="mb-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="w-full md:w-2/3">
          <label className="block text-sm font-medium mb-1">Device</label>
          <select
            className="w-full p-2 border rounded-md"
            value={selectedDevice || ''}
            onChange={(e) => setSelectedDevice(e.target.value)}
          >
            <option value="">Select a device</option>
            {availableDevices.map(device => (
              <option key={device} value={device}>{device}</option>
            ))}
          </select>
        </div>

        <div className="w-full md:w-1/3">
          <div className="text-sm mb-1">Status</div>
          <div className={`font-medium ${
            connectionStatus === 'Connected'
              ? 'text-green-600'
              : connectionStatus === 'Connecting...'
                ? 'text-yellow-600'
                : 'text-red-600'
          }`}>
            {connectionStatus}
          </div>
        </div>
      </div>

      {validDataPoints.length > 0 && (
        <div className="mb-4 p-2 bg-gray-100 rounded-md overflow-auto max-h-28">
          <p className="text-xs font-mono">Latest data: {JSON.stringify(validDataPoints[validDataPoints.length-1])}</p>
        </div>
      )}

      {/* All graphs container */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {metrics.map(metric => renderGraph(metric))}
      </div>

      <div className="mt-6 text-sm text-gray-600">
        <p>Showing {validDataPoints.length} of {maxDataPoints} maximum data points. Data updates in real-time.</p>
      </div>
    </div>
  );
};

export default RealTimeWeatherDashboard;
